import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addSquadMember,
  getMySquads,
  getSquadDetail,
  getSquadMembers,
  getSquadMessages,
  markSquadRead,
  removeSquadMember,
  sendSquadMessage,
  updateSquadMemberRole,
} from '../lib/squads';
import type {
  Squad,
  SquadAddMemberPayload,
  SquadChatMessage,
  SquadChatMessagesResponse,
  SquadMemberSummary,
  SendSquadChatMessagePayload,
  SquadUpdateMemberRolePayload,
} from '../types';

export const squadKeys = {
  all: ['squads'] as const,
  mine: () => [...squadKeys.all, 'mine'] as const,
  detail: (squadId: number | string) => [...squadKeys.all, 'detail', squadId] as const,
  members: (squadId: number | string) => [...squadKeys.all, 'members', squadId] as const,
  messages: (squadId: number | string) => [...squadKeys.all, 'messages', squadId] as const,
};

export function useMySquads(enabled: boolean = true) {
  return useQuery<Squad[]>({
    queryKey: squadKeys.mine(),
    queryFn: getMySquads,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useSquadDetail(squadId: number | string | undefined, enabled: boolean = true) {
  return useQuery<Squad>({
    queryKey: squadId != null ? squadKeys.detail(squadId) : [...squadKeys.all, 'detail', 'missing'],
    queryFn: () => getSquadDetail(squadId as number | string),
    staleTime: 60 * 1000,
    retry: false,
    enabled: enabled && squadId != null,
  });
}

export function useSquadMembers(squadId: number | string | undefined, enabled: boolean = true) {
  const queryClient = useQueryClient();

  const query = useQuery<SquadMemberSummary[]>({
    queryKey: squadId != null ? squadKeys.members(squadId) : [...squadKeys.all, 'members', 'missing'],
    queryFn: () => getSquadMembers(squadId as number | string),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && squadId != null,
  });

  const refreshMembers = async () => {
    if (squadId == null) {
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: squadKeys.members(squadId) }),
      queryClient.invalidateQueries({ queryKey: squadKeys.detail(squadId) }),
      queryClient.invalidateQueries({ queryKey: squadKeys.mine() }),
    ]);
  };

  const addMember = useMutation({
    mutationFn: (payload: SquadAddMemberPayload) => addSquadMember(squadId as number | string, payload),
    onSuccess: () => {
      void refreshMembers();
    },
  });

  const updateRole = useMutation({
    mutationFn: (payload: SquadUpdateMemberRolePayload) => updateSquadMemberRole(squadId as number | string, payload),
    onSuccess: () => {
      void refreshMembers();
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: number) => removeSquadMember(squadId as number | string, userId),
    onSuccess: () => {
      void refreshMembers();
    },
  });

  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    addMember: addMember.mutateAsync,
    updateRole: updateRole.mutateAsync,
    removeMember: removeMember.mutateAsync,
    isAddingMember: addMember.isPending,
    isUpdatingRole: updateRole.isPending,
    isRemovingMember: removeMember.isPending,
  };
}

export function useSquadMessages(
  squadId: number | string | undefined,
  enabled: boolean = true,
  options: { pollMs?: number | false } = {}
) {
  return useQuery<SquadChatMessagesResponse>({
    queryKey: squadId != null ? squadKeys.messages(squadId) : [...squadKeys.all, 'messages', 'missing'],
    queryFn: () => getSquadMessages(squadId as number | string),
    staleTime: 10 * 1000,
    refetchInterval: options.pollMs ?? 15 * 1000,
    refetchIntervalInBackground: false,
    retry: false,
    enabled: enabled && squadId != null,
  });
}

export function useSendSquadMessage(squadId: number | string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<SquadChatMessage, Error, SendSquadChatMessagePayload>({
    mutationFn: (payload) => sendSquadMessage(squadId as number | string, payload),
    onSuccess: () => {
      if (squadId == null) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: squadKeys.messages(squadId) });
      void queryClient.invalidateQueries({ queryKey: squadKeys.detail(squadId) });
      void queryClient.invalidateQueries({ queryKey: squadKeys.mine() });
    },
  });
}

export function useMarkSquadRead(squadId: number | string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (messageId) => markSquadRead(squadId as number | string, messageId),
    onSuccess: () => {
      if (squadId == null) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: squadKeys.detail(squadId) });
      void queryClient.invalidateQueries({ queryKey: squadKeys.mine() });
    },
  });
}
